/** @file mesh.setTriangles / mesh.setHull / mesh.setMirrorVertexMap calls for a topology plan. */
import { sameNumbers } from '@pose2d/commands/mesh/sameNumbers';
import type { CommandCall, Id, MeshAtt } from '@pose2d/format/types';
import type { TopologyPlan } from './blendSources';

/** mesh.setTriangles with edges (null drops a mesh's edges) when they differ or `force`; else nothing. */
function triangleCall(attachment: Id, mesh: MeshAtt, plan: TopologyPlan, force: boolean): CommandCall[] {
  const edges = plan.edges ?? (mesh.edges === undefined ? undefined : null);
  const edgesDiffer = edges !== undefined && !sameNumbers(edges ?? undefined, mesh.edges);
  if (!force && !edgesDiffer && sameNumbers(plan.triangles, mesh.triangles)) return [];
  return [{ name: 'mesh.setTriangles', input: { target: attachment, triangles: plan.triangles, ...(edges === undefined ? {} : { edges }) } }];
}

/** mesh.setMirrorVertexMap re-keyed for a same-count reorder of a mesh that has a map; else nothing. */
function mirrorCall(attachment: Id, mesh: MeshAtt, plan: TopologyPlan): CommandCall[] {
  const map = mesh.mirrorVertexMap;
  const reorder = plan.sources.length === mesh.uvs.length / 2 && plan.sources.every((s) => s.kind === 'old');
  if (map === undefined || !reorder) return [];
  return [{ name: 'mesh.setMirrorVertexMap', input: { target: attachment, map: plan.sources.map((s) => (s.kind === 'old' ? (map[s.v] ?? 0) : 0)) } }];
}

/**
 * Triangles (+ edges) and hull, only where they differ unless `force` (after a vertex-count change, which may have
 * clipped both); a same-count reorder also re-keys the mirror map (the command pairs both sides).
 */
export function shapeCalls(attachment: Id, mesh: MeshAtt, plan: TopologyPlan, force: boolean): CommandCall[] {
  const hull: CommandCall[] = force || plan.hull !== mesh.hull ? [{ name: 'mesh.setHull', input: { target: attachment, hull: plan.hull } }] : [];
  return [...triangleCall(attachment, mesh, plan, force), ...hull, ...mirrorCall(attachment, mesh, plan)];
}
